import { useMemo, useState } from 'react';
import { Linking, Pressable, ScrollView, Text, View, useWindowDimensions } from 'react-native';
import { AlertTriangle, CalendarClock, MessageCircle, Package, PackageX, Clock } from 'lucide-react-native';

import { Badge, Card, EmptyState, SkeletonRows, Tabs } from '@/components/ui';
import { useFirestoreCollection } from '@/hooks/useFirestoreCollection';
import { productosVentaQuery, insumosInternosQuery } from '@/services/firebase/inventario';
import { colors, radius, typography } from '@/theme/tokens';
import { diasHasta, whatsappLink } from '@/utils/format';

type FiltroTipo = 'todas' | 'agotado' | 'bajo' | 'vence_pronto' | 'vencido';

interface Alerta {
  id: string;
  kind: 'agotado' | 'bajo' | 'vencido' | 'vence_pronto';
  producto: any;
  origen: 'venta' | 'interno';
  dias?: number;
}

export function AlertasScreen() {
  const { width } = useWindowDimensions();
  const wide = width >= 920;

  const ventas = useFirestoreCollection(productosVentaQuery);
  const insumos = useFirestoreCollection(insumosInternosQuery);
  const loading = ventas.loading || insumos.loading;
  const [filtro, setFiltro] = useState<FiltroTipo>('todas');

  const alertas = useMemo<Alerta[]>(() => {
    const all: Alerta[] = [];
    ventas.data.forEach((p) => agregarAlertas(p, 'venta', all));
    insumos.data.forEach((p) => agregarAlertas(p, 'interno', all));
    return all;
  }, [ventas.data, insumos.data]);

  const counts = useMemo(() => {
    const c = { todas: alertas.length, agotado: 0, bajo: 0, vence_pronto: 0, vencido: 0 } as Record<FiltroTipo, number>;
    alertas.forEach((a) => {
      c[a.kind] = (c[a.kind] ?? 0) + 1;
    });
    return c;
  }, [alertas]);

  const filtradas = useMemo(() => {
    if (filtro === 'todas') return alertas;
    return alertas.filter((a) => a.kind === filtro);
  }, [alertas, filtro]);

  return (
    <ScrollView contentContainerStyle={{ padding: wide ? 28 : 16, gap: 18, paddingBottom: 80 }}>
      <Tabs
        active={filtro}
        onChange={(v) => setFiltro(v as FiltroTipo)}
        items={[
          { id: 'todas', label: 'Todas', badge: counts.todas, badgeTone: 'magenta' },
          { id: 'agotado', label: 'Agotados', icon: PackageX, badge: counts.agotado, badgeTone: 'danger' },
          { id: 'bajo', label: 'Stock bajo', icon: AlertTriangle, badge: counts.bajo, badgeTone: 'warning' },
          { id: 'vence_pronto', label: 'Vencen pronto', icon: Clock, badge: counts.vence_pronto, badgeTone: 'warning' },
          { id: 'vencido', label: 'Vencidos', icon: CalendarClock, badge: counts.vencido, badgeTone: 'danger' },
        ]}
      />

      {loading ? (
        <SkeletonRows rows={6} />
      ) : filtradas.length === 0 ? (
        <Card>
          <EmptyState
            illustration="cat"
            title={filtro === 'todas' ? 'Todo ronroneando' : 'Sin alertas acá'}
            message="Tu clínica está al día. Buen trabajo."
          />
        </Card>
      ) : (
        <View style={{ gap: 8 }}>
          {filtradas.map((a) => (
            <AlertaCard key={a.id} alerta={a} />
          ))}
        </View>
      )}
    </ScrollView>
  );
}

function agregarAlertas(p: any, origen: 'venta' | 'interno', acc: Alerta[]) {
  if (p.stock === 0) {
    acc.push({ id: `agotado-${origen}-${p.id}`, kind: 'agotado', producto: p, origen });
  } else if (p.stockBajo) {
    acc.push({ id: `bajo-${origen}-${p.id}`, kind: 'bajo', producto: p, origen });
  }
  const v = p.vencimiento?.toDate?.();
  const d = diasHasta(v ?? null);
  if (d !== null && d <= 60) {
    acc.push({
      id: `venc-${origen}-${p.id}`,
      kind: d < 0 ? 'vencido' : 'vence_pronto',
      producto: p,
      origen,
      dias: d,
    });
  }
}

function AlertaCard({ alerta }: { alerta: Alerta }) {
  const tone = alerta.kind === 'agotado' || alerta.kind === 'vencido' ? colors.danger : colors.warning;

  const titulo =
    alerta.kind === 'agotado'
      ? `${alerta.producto.nombre} agotado`
      : alerta.kind === 'bajo'
        ? `${alerta.producto.nombre} con stock bajo`
        : alerta.kind === 'vencido'
          ? `${alerta.producto.nombre} vencido`
          : `${alerta.producto.nombre} vence en ${alerta.dias}d`;

  const detalle =
    alerta.kind === 'bajo'
      ? `Quedan ${alerta.producto.stock} · mínimo ${alerta.producto.stockMin} · ${alerta.producto.presentacion}`
      : alerta.producto.presentacion;

  const Icon = alerta.kind === 'vencido' || alerta.kind === 'vence_pronto' ? CalendarClock : alerta.kind === 'agotado' ? Package : AlertTriangle;

  const onWhatsapp = () => {
    if (!alerta.producto.proveedorWhatsapp) return;
    const msg = `Hola${alerta.producto.proveedorNombre ? ` ${alerta.producto.proveedorNombre}` : ''}, te escribo de CliniCAT. Necesito reponer ${alerta.producto.nombre} (${alerta.producto.presentacion}).`;
    Linking.openURL(whatsappLink(alerta.producto.proveedorWhatsapp, msg));
  };

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 14,
        padding: 16,
        borderRadius: radius['2xl'],
        backgroundColor: colors.white,
        borderWidth: 1,
        borderColor: alerta.kind === 'agotado' || alerta.kind === 'vencido' ? '#FBD0D0' : '#FCE7B5',
      }}
    >
      <Icon size={22} color={tone[600]} />
      <View style={{ flex: 1, gap: 4 }}>
        <Text style={{ fontWeight: typography.weights.bold, color: colors.ink[900], fontSize: 15 }} numberOfLines={1}>
          {titulo}
        </Text>
        <Text style={{ color: colors.ink[500], fontSize: 12 }} numberOfLines={1}>
          {detalle}
        </Text>
      </View>
      <Badge label={alerta.origen === 'venta' ? 'Venta' : 'Interno'} tone="neutral" />
      {alerta.producto.proveedorWhatsapp ? (
        <Pressable
          onPress={onWhatsapp}
          style={({ hovered }) => ({
            flexDirection: 'row',
            alignItems: 'center',
            gap: 6,
            paddingHorizontal: 12,
            height: 36,
            borderRadius: 12,
            backgroundColor: hovered ? '#10AC84' : '#0F9D58',
            // @ts-ignore web only
            cursor: 'pointer',
          })}
        >
          <MessageCircle size={14} color={colors.white} />
          <Text style={{ color: colors.white, fontWeight: typography.weights.bold, fontSize: 13 }}>Contactar</Text>
        </Pressable>
      ) : null}
    </View>
  );
}
